import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  addDays,
  formatDate,
  readableInk,
  todayIn,
  type JobStatus,
  type JobSummary,
} from '@jobshop/shared';
import {
  useArchiveFinishedJobs,
  useCreateJob,
  useJobs,
  usePatchJob,
  useShopTimeZone,
  useTemplates,
} from '../../api/hooks';
import { useSession } from '../../auth/useUser';
import {
  BadgeChip,
  Card,
  CardHeader,
  ChipLink,
  Empty,
  ErrorNote,
  Field,
  Modal,
  Pill,
  Spinner,
} from '../../components/ui';

type SortKey = 'delivery' | 'priority' | 'name';

/**
 * Two lists, never mixed: the shop floor ("Current") and the archive. Old jobs are put
 * away rather than deleted — their plan, parts, scans and list stay readable, and one
 * click brings a job back — so the current list stays the handful a planner actually
 * works on. The archive is somewhere you go to look something up, hence its own view
 * with a search box rather than another status in the filter.
 */
type View = 'current' | 'archived';

export function JobsPage() {
  const [view, setView] = useState<View>('current');
  const [status, setStatus] = useState<JobStatus | ''>('');
  const [sort, setSort] = useState<SortKey>('delivery');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [archivingFinished, setArchivingFinished] = useState(false);
  const session = useSession();
  const archived = view === 'archived';
  const jobs = useJobs({ ...(status ? { status } : {}), archived });

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const list = (jobs.data ?? []).filter(
      (j) =>
        !needle ||
        j.name.toLowerCase().includes(needle) ||
        (j.customer ?? '').toLowerCase().includes(needle),
    );
    list.sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'priority') return b.priority - a.priority;
      if (archived) {
        // Most recently put away first: what you archived last week is what you are
        // most likely to be coming back for.
        return (b.archivedAt ? new Date(b.archivedAt).getTime() : 0) -
          (a.archivedAt ? new Date(a.archivedAt).getTime() : 0);
      }
      return (a.dateDelivery ?? '9999').localeCompare(b.dateDelivery ?? '9999');
    });
    return list;
  }, [jobs.data, sort, search, archived]);

  // Finished work still sitting on the current list — what "Archive finished jobs"
  // would put away. Counted from the unfiltered list so the number is honest whatever
  // the status filter says.
  const finishedCount = archived
    ? 0
    : (jobs.data ?? []).filter((j) => j.status === 'done' || j.status === 'cancelled').length;

  const count = jobs.data?.length ?? 0;
  const subtitle = !jobs.data
    ? undefined
    : search.trim() && rows.length !== count
      ? `${rows.length} of ${count} ${archived ? 'archived ' : ''}job${count === 1 ? '' : 's'}`
      : `${count} ${archived ? 'archived ' : ''}job${count === 1 ? '' : 's'}`;

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader
          title={archived ? 'Archived jobs' : 'Jobs'}
          subtitle={subtitle}
          actions={
            <>
              <ViewSwitch view={view} onChange={setView} />
              <input
                type="search"
                className="field w-44"
                placeholder="Find a job…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Find a job by name or customer"
              />
              <select
                className="field w-auto"
                value={status}
                onChange={(e) => setStatus(e.target.value as JobStatus | '')}
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="done">Done</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                className="field w-auto"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                aria-label="Sort by"
              >
                <option value="delivery">{archived ? 'Recently archived' : 'By delivery date'}</option>
                <option value="priority">By priority</option>
                <option value="name">By name</option>
              </select>
              {session.can('planner') && !archived && finishedCount > 0 ? (
                <button
                  className="btn-secondary"
                  onClick={() => setArchivingFinished(true)}
                  title="Put every done or cancelled job in the archive. Nothing is deleted, and a job can be restored at any time."
                >
                  Archive finished jobs
                </button>
              ) : null}
              {session.can('planner') && !archived ? (
                <button className="btn-primary" onClick={() => setCreating(true)}>
                  New job
                </button>
              ) : null}
            </>
          }
        />

        {archived ? (
          <p className="border-b border-ink-100 bg-ink-50 px-4 py-2 text-xs text-ink-600">
            Archived jobs are kept in full — plan, list, parts and scans — and left out of
            the current list and of <em>Re-plan all jobs</em>. Restore one to bring it back.
          </p>
        ) : null}

        {jobs.isLoading ? <Spinner /> : null}
        {jobs.error ? (
          <div className="p-4">
            <ErrorNote error={jobs.error} />
          </div>
        ) : null}
        {jobs.data?.length === 0 ? (
          <Empty>
            {archived
              ? status
                ? `No archived ${status} jobs.`
                : 'Nothing archived yet. Archive a job from its Plan page, or every finished job at once from the current list.'
              : status
                ? `No ${status} jobs.`
                : 'No jobs yet.'}
          </Empty>
        ) : null}
        {jobs.data && jobs.data.length > 0 && rows.length === 0 ? (
          <Empty>No jobs match “{search.trim()}”.</Empty>
        ) : null}

        {rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Job</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th
                    className="px-4 py-2 font-medium"
                    title="Higher goes first when jobs compete for the same capacity."
                  >
                    Priority
                  </th>
                  <th className="px-4 py-2 font-medium">Delivery</th>
                  <th className="px-4 py-2 font-medium">Planned finish</th>
                  <th className="px-4 py-2 font-medium">Parts tracked</th>
                  <th className="px-4 py-2 font-medium">{archived ? 'Archived' : 'Flags'}</th>
                  {archived && session.can('planner') ? <th className="px-4 py-2" /> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((job) => (
                  <JobRow key={job._id} job={job} archived={archived} />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      <NewJobModal open={creating} onClose={() => setCreating(false)} />
      <ArchiveFinishedModal
        open={archivingFinished}
        count={finishedCount}
        onClose={() => setArchivingFinished(false)}
      />
    </div>
  );
}

/** Current / Archived, as two pressed-or-not buttons rather than a dropdown: it is the
 * one choice on this page that changes what the whole table *is*. */
function ViewSwitch({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  const options: { key: View; label: string }[] = [
    { key: 'current', label: 'Current' },
    { key: 'archived', label: 'Archived' },
  ];
  return (
    <span className="inline-flex overflow-hidden rounded-md border border-ink-300" role="group">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          aria-pressed={view === o.key}
          onClick={() => onChange(o.key)}
          className={
            view === o.key
              ? 'bg-ink-900 px-3 py-1.5 text-sm font-medium text-white'
              : 'bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-100'
          }
        >
          {o.label}
        </button>
      ))}
    </span>
  );
}

function JobRow({ job, archived }: { job: JobSummary; archived: boolean }) {
  const session = useSession();
  const timeZone = useShopTimeZone();
  const patchJob = usePatchJob(job._id);
  const late = job.finishDate && job.dateDelivery && job.finishDate > job.dateDelivery;
  // Deduplicate: several batches can raise the same badge code.
  const codes = [...new Set(job.badges.map((b) => b.code))];

  return (
    <tr className="border-b border-ink-100 last:border-0 hover:bg-ink-50">
      <td className="px-4 py-2">
        {/* The name goes to the List page — what a customer opens far more often
            than the plan. "Plan" stays one deliberate click away, not a misclick. */}
        <Link to={`/jobs/${job._id}/list`} className="hover:underline">
          <span
            className="rounded px-1.5 py-0.5 font-medium"
            style={{ backgroundColor: job.color, color: readableInk(job.color) }}
          >
            {job.name}
          </span>
        </Link>
        {/* The same four pages the job's own header offers, in the same order, so the
            row is a shortcut into a job rather than a different set of choices. */}
        <ChipLink to={`/jobs/${job._id}`} className="ml-2" title="Open the plan">
          Plan
        </ChipLink>
        <ChipLink to={`/jobs/${job._id}/list`} className="ml-2" title="Open the list">
          List
        </ChipLink>
        {job.soleBatchId ? (
          <ChipLink
            to={`/jobs/${job._id}/batches/${job.soleBatchId}/parts`}
            className="ml-2"
            title="Open the parts page"
          >
            Parts
          </ChipLink>
        ) : null}
        <ChipLink to={`/jobs/${job._id}/scans`} className="ml-2" title="Open the scans">
          Scans
        </ChipLink>
        {job.customer ? <span className="ml-2 text-xs text-ink-500">{job.customer}</span> : null}
        {job.batchCount > 1 ? (
          <span className="ml-2 text-xs text-ink-500">{job.batchCount} batches</span>
        ) : null}
        {job.openListItemCount > 0 ? (
          <span
            className="ml-2 text-xs text-ink-500"
            title={`${job.openListItemCount} open item${job.openListItemCount === 1 ? '' : 's'} on the list`}
          >
            {job.openListItemCount} open
          </span>
        ) : null}
        {job.noteCount > 0 ? (
          <span
            className="ml-2 text-xs text-ink-500"
            title={`${job.noteCount} note${job.noteCount === 1 ? '' : 's'} — open the job to read them`}
          >
            {job.noteCount} note{job.noteCount === 1 ? '' : 's'}
          </span>
        ) : null}
      </td>
      <td className="px-4 py-2">
        <Pill tone={job.status === 'active' ? 'info' : job.status === 'done' ? 'good' : 'neutral'}>
          {job.status}
        </Pill>
      </td>
      <td className="px-4 py-2 tabular-nums text-ink-600">{job.priority}</td>
      <td className="px-4 py-2 text-ink-600">
        {job.dateDelivery ? formatDate(job.dateDelivery) : '—'}
      </td>
      <td className="px-4 py-2">
        {job.finishDate ? (
          <span className={late ? 'font-medium text-red-700' : 'text-ink-600'}>
            {formatDate(job.finishDate)}
          </span>
        ) : (
          <span className="text-ink-400">not planned</span>
        )}
      </td>
      <td className="px-4 py-2 text-ink-600">
        {/* Coverage stated honestly: most parts carry no label. */}
        {job.partsTotal === 0 ? '—' : `${job.partsTracked} of ${job.partsTotal}`}
      </td>
      {archived ? (
        <td className="px-4 py-2 text-ink-600">
          {/* An instant becomes a calendar day only through the shop's timezone. */}
          {job.archivedAt ? formatDate(todayIn(timeZone, new Date(job.archivedAt))) : '—'}
          {job.archivedBy ? (
            <span className="ml-1 text-xs text-ink-500" title={job.archivedBy}>
              by {job.archivedBy.split('@')[0]}
            </span>
          ) : null}
        </td>
      ) : (
        <td className="px-4 py-2">
          <div className="flex flex-wrap gap-1">
            {codes.map((code) => {
              const badge = job.badges.find((b) => b.code === code)!;
              return <BadgeChip key={code} badge={badge} />;
            })}
          </div>
        </td>
      )}
      {archived && session.can('planner') ? (
        <td className="px-4 py-2 text-right">
          <button
            className="btn-secondary"
            disabled={patchJob.isPending}
            onClick={() => patchJob.mutate({ archived: false })}
            title="Bring this job back to the current list. Its status, plan and bookings are untouched."
          >
            {patchJob.isPending ? 'Restoring…' : 'Restore'}
          </button>
        </td>
      ) : null}
    </tr>
  );
}

function ArchiveFinishedModal({
  open,
  count,
  onClose,
}: {
  open: boolean;
  count: number;
  onClose: () => void;
}) {
  const archive = useArchiveFinishedJobs();
  return (
    <Modal open={open} onClose={onClose} title="Archive finished jobs">
      <p className="text-sm text-ink-700">
        Put {count === 1 ? 'the one' : `all ${count}`} done or cancelled job
        {count === 1 ? '' : 's'} in the archive? Nothing is deleted: each one stays readable
        under <strong>Archived</strong> and can be restored at any time. Active and draft jobs
        are not touched.
      </p>
      <ErrorNote error={archive.error} />
      <div className="flex justify-end gap-2 pt-3">
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={archive.isPending}
          onClick={() => archive.mutate(undefined, { onSuccess: onClose })}
        >
          {archive.isPending ? 'Archiving…' : `Archive ${count} job${count === 1 ? '' : 's'}`}
        </button>
      </div>
    </Modal>
  );
}

function NewJobModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const templates = useTemplates();
  const create = useCreateJob();
  const today = todayIn(useShopTimeZone());
  const [form, setForm] = useState({
    name: '',
    customer: '',
    templateName: '',
    dateStart: today,
    dateDelivery: addDays(today, 60),
    priority: 1000,
  });

  const templateName = form.templateName || templates.data?.[0]?.name || '';

  return (
    <Modal open={open} onClose={onClose} title="New job">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(
            {
              name: form.name.trim(),
              ...(form.customer.trim() ? { customer: form.customer.trim() } : {}),
              templateName,
              dateStart: form.dateStart,
              dateDelivery: form.dateDelivery,
              priority: form.priority,
              status: 'active',
            },
            { onSuccess: onClose },
          );
        }}
      >
        <Field
          label="Job name"
          hint="Printed QR labels embed this, so it cannot be changed lightly."
        >
          <input
            className="field"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Customer">
          <input
            className="field"
            value={form.customer}
            onChange={(e) => setForm({ ...form, customer: e.target.value })}
          />
        </Field>
        <Field label="Template" hint="Seeds the hours; edit them per batch before scheduling.">
          <select
            className="field"
            value={templateName}
            onChange={(e) => setForm({ ...form, templateName: e.target.value })}
          >
            {(templates.data ?? []).map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start">
            <input
              type="date"
              className="field"
              value={form.dateStart}
              onChange={(e) => setForm({ ...form, dateStart: e.target.value })}
            />
          </Field>
          <Field label="Delivery">
            <input
              type="date"
              className="field"
              value={form.dateDelivery}
              onChange={(e) => setForm({ ...form, dateDelivery: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Priority" hint="Higher wins when batches compete in a re-plan.">
          <input
            type="number"
            className="field"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
          />
        </Field>

        <ErrorNote error={create.error} />

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create job'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
